import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import {
  createSupportTicket,
  fetchUserTickets
} from "../store/thunks/helpDeskThunk";
import {
  closeModal,
  clearError,
  setConsoleLogs,
  clearTicketsError
} from "../store/slices/helpDeskSlice";
import consoleCapture from "../utils/consoleCapture";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

const HelpDeskModal = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { status, error, isModalOpen, userTickets, ticketsStatus, ticketsError } = useSelector((state) => state.helpDesk);
  
  // Form state
  const [formData, setFormData] = useState({
    summary: '',
    description: '',
    priority: 'Medium',
    issueType: 'General Question',
    includeConsoleLogs: true
  });

  const [formErrors, setFormErrors] = useState({});
  const [currentView, setCurrentView] = useState('form'); // 'form' | 'tickets'

  // Clear form when modal opens/closes
  useEffect(() => {
    if (isModalOpen) {
      setFormData({
        summary: '',
        description: '',
        priority: 'Medium',
        issueType: 'General Question',
        includeConsoleLogs: true
      });
      setFormErrors({});
      setCurrentView('form');
      dispatch(clearError());
      dispatch(clearTicketsError());
    }
  }, [isModalOpen, dispatch]);

  // Handle successful ticket creation
  useEffect(() => {
    if (status === 'succeeded') {
      toast.success('Support ticket created successfully! You will receive updates via email.');
    }
  }, [status]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(`Failed to create support ticket: ${error}`);
    }
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    
    // If issue type is changing and it's not Bug Report or Technical Issue, disable console logs
    if (name === 'issueType' && value !== 'Bug Report' && value !== 'Technical Issue') {
      newFormData.includeConsoleLogs = false;
    }
    // If switching to Bug Report or Technical Issue, default to enabled
    else if (name === 'issueType' && (value === 'Bug Report' || value === 'Technical Issue')) {
      newFormData.includeConsoleLogs = true;
    }
    
    setFormData(newFormData);
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.summary.trim()) {
      errors.summary = 'Issue summary is required';
    } else if (formData.summary.length < 10) {
      errors.summary = 'Summary must be at least 10 characters';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Issue description is required';
    } else if (formData.description.length < 20) {
      errors.description = 'Description must be at least 20 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      console.log('[Help Desk Modal] Preparing ticket submission');
      
      // Prepare console logs if requested with size limiting
      let consoleLogs = null;
      if (formData.includeConsoleLogs) {
        consoleLogs = consoleCapture.getFormattedLogs(15, 30); // 15 entries, max 30KB
        dispatch(setConsoleLogs(consoleLogs));
      }

      // Get current React route instead of full URL
      const currentRoute = window.location.pathname + window.location.search + window.location.hash;
      const baseUrl = window.location.origin;
      
      // Prepare ticket data
      const ticketData = {
        summary: formData.summary.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        issueType: formData.issueType,
        consoleLogs: consoleLogs,
        userAgent: navigator.userAgent,
        url: window.location.href, // Keep full URL for compatibility
        route: currentRoute, // Add React route for better SPA context
        baseUrl: baseUrl // Add base URL for reference
      };

      console.log('[Help Desk Modal] Submitting ticket with data:', {
        ...ticketData,
        consoleLogs: consoleLogs ? `${consoleLogs.length} characters of logs` : 'none'
      });

      // Dispatch the create ticket action
      await dispatch(createSupportTicket(ticketData));
      
    } catch (error) {
      console.error('[Help Desk Modal] Error submitting ticket:', error);
      toast.error('Unexpected error creating support ticket');
    }
  };

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleViewTickets = async () => {
    console.log('[Help Desk Modal] Switching to tickets view');
    setCurrentView('tickets');
    dispatch(fetchUserTickets());
  };

  const handleBackToForm = () => {
    console.log('[Help Desk Modal] Switching back to form view');
    setCurrentView('form');
  };

  const priorityOptions = [
    { value: 'Highest', label: 'Highest - System Down', color: 'text-red-600' },
    { value: 'High', label: 'High - Critical Issue', color: 'text-orange-600' },
    { value: 'Medium', label: 'Medium - Standard Issue', color: 'text-yellow-600' },
    { value: 'Low', label: 'Low - Minor Issue', color: 'text-blue-600' },
    { value: 'Lowest', label: 'Lowest - Enhancement', color: 'text-gray-600' }
  ];

  const issueTypeOptions = [
    { value: 'General Question', label: 'General Question - General support inquiry' },
    { value: 'Bug Report', label: 'Bug Report - Something is broken or not working' },
    { value: 'Feature Request', label: 'Feature Request - Request new functionality' },
    { value: 'Technical Issue', label: 'Technical Issue - Performance, login, connectivity problems' },
    { value: 'Account & User Management', label: 'Account & User Management - User accounts, permissions, clinic setup' },
    { value: 'Integration Support', label: 'Integration Support - QuickBooks, SMS, email integration issues' },
    { value: 'Billing & Data Questions', label: 'Billing & Data Questions - Invoice issues, payment questions, reports' },
    { value: 'Training & How-To', label: 'Training & How-To - Help using features, best practices' }
  ];

  // Render tickets view
  const renderTicketsView = () => (
    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-gray-900">Your Open Support Tickets</h4>
        <button
          onClick={handleBackToForm}
          className="text-sm text-blue-600 hover:text-blue-800 transition-colors">
          ← Create New Ticket
        </button>
      </div>

      {ticketsStatus === 'loading' && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading tickets...</span>
        </div>
      )}

      {ticketsError && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-4">
          <p className="text-sm text-red-800">
            <strong>Error:</strong> {ticketsError}
          </p>
        </div>
      )}

      {ticketsStatus === 'succeeded' && (
        <>
          {userTickets.length === 0 ? (
            <div className="text-center py-8">
              <QuestionMarkCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">You don't have any open support tickets.</p>
              <button
                onClick={handleBackToForm}
                className="text-blue-600 hover:text-blue-800 transition-colors">
                Create your first support ticket
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {userTickets.map((ticket) => (
                <div key={ticket.ticketId} className="border border-gray-200 rounded-sm p-4 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-medium text-gray-900 text-sm">
                      {ticket.ticketId}: {ticket.summary}
                    </h5>
                    <span className={`px-2 py-1 text-xs rounded-sm ${
                      ticket.statusCategory === 'Done' ? 'bg-green-100 text-green-800' :
                      ticket.statusCategory === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {ticket.status}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600 mb-3">
                    <p><strong>Type:</strong> {ticket.issueType}</p>
                    <p><strong>Created:</strong> {ticket.created}</p>
                    {ticket.updated && ticket.updated !== ticket.created && (
                      <p><strong>Updated:</strong> {ticket.updated}</p>
                    )}
                  </div>

                  {ticket.description && (
                    <div className="bg-gray-50 rounded-sm p-3 mt-2">
                      <p className="text-xs font-medium text-gray-700 mb-1">Description:</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {ticket.description.length > 200
                          ? `${ticket.description.substring(0, 200)}...`
                          : ticket.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border rounded-sm shadow-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 flex justify-between items-center border-b flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <QuestionMarkCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {currentView === 'form' ? 'Contact Support' : 'My Support Tickets'}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            {currentView === 'form' && (
              <button
                onClick={handleViewTickets}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors px-2 py-1 rounded">
                View My Tickets
              </button>
            )}
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-900 transition-colors p-1">
              <span className="sr-only">Close</span>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content Area */}
        {currentView === 'tickets' ? renderTicketsView() : (
          <div className="flex-1 overflow-y-auto">
            <form id="help-desk-form" onSubmit={handleSubmit} className="px-4 sm:px-6 py-4 space-y-4 sm:space-y-6">
          {/* Issue Summary */}
          <div>
            <label htmlFor="summary" className="block text-sm font-medium text-gray-700 mb-2">
              Issue Summary <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="summary"
              name="summary"
              value={formData.summary}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.summary ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Brief description of the issue..."
            />
            {formErrors.summary && (
              <p className="mt-1 text-sm text-red-600">{formErrors.summary}</p>
            )}
          </div>

          {/* Issue Type */}
          <div>
            <label htmlFor="issueType" className="block text-sm font-medium text-gray-700 mb-2">
              Issue Type <span className="text-red-500">*</span>
            </label>
            <select
              id="issueType"
              name="issueType"
              value={formData.issueType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {issueTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
              Priority <span className="text-red-500">*</span>
            </label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {priorityOptions.map(option => (
                <option key={option.value} value={option.value} className={option.color}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-3 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                formErrors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Please provide as much detail as possible about the issue. Include steps to reproduce, error messages, and expected vs actual behavior..."
            />
            {formErrors.description && (
              <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
            )}
          </div>

          {/* Console Logs Option - Only show for Bug Report and Technical Issue */}
          {(formData.issueType === 'Bug Report' || formData.issueType === 'Technical Issue') && (
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="includeConsoleLogs"
                name="includeConsoleLogs"
                checked={formData.includeConsoleLogs}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="includeConsoleLogs" className="text-sm text-gray-700">
                Include browser console logs for debugging
                <p className="text-xs text-gray-500 mt-1">
                  Console logs help our technical team diagnose issues faster. Sensitive data is automatically filtered out.
                </p>
              </label>
            </div>
          )}

          {/* System Information Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
            <p className="text-xs sm:text-sm text-blue-800">
              <strong>Automatic Information:</strong> Your user details, browser information, and current page will be included to help resolve your issue quickly.
            </p>
          </div>
            </form>
          </div>
        )}

        {/* Form Actions - Fixed at bottom - Only show in form view */}
        {currentView === 'form' && (
          <div className="border-t bg-gray-50 px-4 sm:px-6 py-3 flex flex-col-reverse sm:flex-row sm:justify-end space-y-reverse space-y-2 sm:space-y-0 sm:space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
            Cancel
          </button>
          <button
            type="submit"
            form="help-desk-form"
            disabled={status === 'loading'}
            className={`w-full sm:w-auto px-4 py-2 text-sm font-medium text-white rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              status === 'loading'
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}>
            {status === 'loading' ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Ticket...
              </div>
            ) : (
              'Create Support Ticket'
            )}
          </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpDeskModal;